import React from 'react';
import { Github, Linkedin, Twitter, Link as LinkIcon, Mail, MapPin, Phone, ExternalLink, Code, Briefcase } from 'lucide-react';
import { motion } from 'framer-motion';

const fadeIn = {
  initial: { opacity: 0, y: 15 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5 }
};

export default function RetroTheme({ profileData, socials, activeColor, trackSocialClick, trackDownload }) {
  const { core, education = [], experience = [], projects = [], skills = [] } = profileData;

  return (
    <div className="min-h-screen font-mono selection:bg-amber-400 selection:text-amber-900"
      style={{ background: 'linear-gradient(180deg, #1a1510 0%, #231d15 50%, #1a1510 100%)' }}
    >
      {/* Scanline overlay */}
      <div className="fixed inset-0 z-0 pointer-events-none opacity-[0.03]"
        style={{ backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(255,200,100,0.1) 2px, rgba(255,200,100,0.1) 4px)' }}
      />

      {/* CRT vignette */}
      <div className="fixed inset-0 z-0 pointer-events-none"
        style={{ boxShadow: 'inset 0 0 150px rgba(0,0,0,0.6)' }}
      />

      {/* Hero */}
      <section className="relative z-10 min-h-[80vh] flex items-center justify-center px-6">
        <div className="text-center space-y-6 max-w-2xl">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            className="inline-block px-4 py-1.5 rounded border-2 border-dashed border-amber-600/40 text-amber-500 text-xs font-bold uppercase tracking-[0.3em]"
          >
            ▸ PORTFOLIO v1.0
          </motion.div>

          <motion.h1 initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.2 }}
            className="text-4xl sm:text-6xl font-black text-amber-400 tracking-tight"
            style={{ textShadow: '2px 2px 0 #92400e, 4px 4px 0 rgba(146,64,14,0.3)' }}
          >
            {core.name}
          </motion.h1>

          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}
            className="text-amber-200/50 text-sm leading-relaxed max-w-md mx-auto"
          >
            {core.summary || 'A creative professional.'}
          </motion.p>

          {/* Retro ASCII divider */}
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }}
            className="text-amber-700/40 text-xs tracking-[0.5em]"
          >
            ═══════════════════
          </motion.div>

          {/* Social links */}
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}
            className="flex justify-center gap-3"
          >
            {socials.github && <a href={socials.github} target="_blank" rel="noreferrer" onClick={() => trackSocialClick('github')} className="p-2.5 border-2 border-amber-700/30 rounded text-amber-500/70 hover:text-amber-400 hover:border-amber-600/50 hover:bg-amber-500/5 transition-all"><Github className="w-5 h-5" /></a>}
            {socials.linkedin && <a href={socials.linkedin} target="_blank" rel="noreferrer" onClick={() => trackSocialClick('linkedin')} className="p-2.5 border-2 border-amber-700/30 rounded text-amber-500/70 hover:text-amber-400 hover:border-amber-600/50 hover:bg-amber-500/5 transition-all"><Linkedin className="w-5 h-5" /></a>}
            {socials.twitter && <a href={socials.twitter} target="_blank" rel="noreferrer" onClick={() => trackSocialClick('twitter')} className="p-2.5 border-2 border-amber-700/30 rounded text-amber-500/70 hover:text-amber-400 hover:border-amber-600/50 hover:bg-amber-500/5 transition-all"><Twitter className="w-5 h-5" /></a>}
            {socials.custom && <a href={socials.custom} target="_blank" rel="noreferrer" onClick={() => trackSocialClick('custom')} className="p-2.5 border-2 border-amber-700/30 rounded text-amber-500/70 hover:text-amber-400 hover:border-amber-600/50 hover:bg-amber-500/5 transition-all"><LinkIcon className="w-5 h-5" /></a>}
          </motion.div>
        </div>
      </section>

      {/* Experience */}
      {experience.length > 0 && (
        <section className="relative z-10 max-w-3xl mx-auto px-6 py-16">
          <motion.div {...fadeIn} className="mb-8">
            <h2 className="text-xs text-amber-600 uppercase tracking-[0.4em] font-bold">▸ WORK_HISTORY.log</h2>
            <div className="w-full h-px bg-gradient-to-r from-amber-700/40 to-transparent mt-2" />
          </motion.div>

          <div className="space-y-1">
            {experience.map((exp, i) => (
              <motion.div key={i} {...fadeIn}
                className="p-4 border-l-2 border-amber-700/30 hover:border-amber-500/50 hover:bg-amber-500/[0.02] transition-all"
              >
                <div className="text-[10px] text-amber-600/50 font-bold tracking-wider">[{exp.start_date} → {exp.end_date || 'PRESENT'}]</div>
                <h3 className="text-sm font-bold text-amber-300 mt-1">{exp.job_title}</h3>
                <div className="text-xs text-amber-500/60 font-bold">@ {exp.company}</div>
                <p className="text-xs text-amber-200/30 mt-2 leading-relaxed">{exp.description}</p>
              </motion.div>
            ))}
          </div>
        </section>
      )}

      {/* Projects */}
      {projects.length > 0 && (
        <section className="relative z-10 max-w-4xl mx-auto px-6 py-16">
          <motion.div {...fadeIn} className="mb-8">
            <h2 className="text-xs text-amber-600 uppercase tracking-[0.4em] font-bold">▸ PROJECTS.dat</h2>
            <div className="w-full h-px bg-gradient-to-r from-amber-700/40 to-transparent mt-2" />
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {projects.map((proj, i) => (
              <motion.div key={i} {...fadeIn}
                className="p-5 border border-amber-700/20 rounded hover:border-amber-600/40 hover:bg-amber-500/[0.02] transition-all"
              >
                <h3 className="text-sm font-bold text-amber-300 flex items-center gap-2">
                  <span className="text-amber-600/50">▹</span> {proj.title}
                </h3>
                <p className="text-xs text-amber-200/30 mt-2 leading-relaxed">{proj.description}</p>
                {proj.link && (
                  <a href={proj.link} target="_blank" rel="noreferrer"
                    className="inline-flex items-center gap-1 text-[10px] font-bold text-amber-500/60 hover:text-amber-400 mt-3 uppercase tracking-wider">
                    [OPEN] <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </motion.div>
            ))}
          </div>
        </section>
      )}

      {/* Skills */}
      {skills.length > 0 && (
        <section className="relative z-10 max-w-3xl mx-auto px-6 py-16">
          <motion.div {...fadeIn} className="mb-8">
            <h2 className="text-xs text-amber-600 uppercase tracking-[0.4em] font-bold">▸ SKILLS.cfg</h2>
            <div className="w-full h-px bg-gradient-to-r from-amber-700/40 to-transparent mt-2" />
          </motion.div>

          <motion.div {...fadeIn} className="flex flex-wrap gap-2">
            {skills.map((sk, i) => (
              <span key={i} className="px-3 py-1.5 border border-amber-700/20 rounded text-xs text-amber-400/60 font-bold hover:border-amber-600/40 hover:text-amber-400 transition-all">
                {sk.name || sk}
              </span>
            ))}
          </motion.div>
        </section>
      )}

      {/* Footer */}
      <section className="relative z-10 max-w-2xl mx-auto px-6 py-16 text-center space-y-4">
        <div className="text-amber-700/30 text-xs tracking-[0.5em]">═══════════════════</div>
        <div className="flex flex-wrap justify-center gap-6 text-[11px] text-amber-500/40">
          {core.email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{core.email}</span>}
          {core.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{core.phone}</span>}
          {core.city && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{core.city}</span>}
        </div>
        <p className="text-[9px] text-amber-700/30 tracking-wider pt-4">[ SYSTEM POWERED BY CVLOGS © {new Date().getFullYear()} ]</p>
      </section>
    </div>
  );
}
